import { format } from "date-fns";
import { responseStreamHelper } from ".";
import {
  AdzapService,
  EmailPersonalizadoService,
  MensagemPersonalizadaService,
  NotificacaoService,
  notification,
  PlugBoletoService,
} from "../services";
import { formatPrice, responseErros } from "../utils";
import { PDFDocument } from "pdf-lib";

export class BoletosHelper {
  constructor() {
    this.mensagemPersonalizadaService = new MensagemPersonalizadaService();
    this.emailPersonalizadoService = new EmailPersonalizadoService();
    this.notificacaoService = new NotificacaoService();
    this.plugBoletoService = new PlugBoletoService();
    this.adzapService = new AdzapService();
  }

  async buscarMensagemContexto(boleto, dadosEmpresa) {
    let text = "Segue boleto solicitado em anexo";
    const getPrimeiroNome = (nomeCompleto) => {
      return nomeCompleto?.split(" ")[0] || "";
    };
    const { tituloDocumento, status, dataVencimento, valorTotal } = boleto;
    const texto = await this.mensagemPersonalizadaService.getMensagem({
      contexto: "boleto",
      apelidoFantasia: dadosEmpresa.empresa.fantasia,
      nomeCompletoCliente: boleto.cliente?.nomeRazaoSocial,
      primeiroNomeCliente: getPrimeiroNome(boleto.cliente?.nomeRazaoSocial),
      nomeUsuario: dadosEmpresa.usuario.nome,
      tituloDocumento,
      status,
      dataVencimento: format(new Date(dataVencimento), "dd/MM/yyyy"),
      valorTotal: formatPrice(valorTotal),
    });

    if (!texto.isAxiosError) {
      text = texto.data;
    } else {
      responseErros(texto);
      throw new Error("Erro ao buscar mensagem personalizada");
    }

    return text;
  }

  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async formatarMensagemAdzap(boleto, celular, dadosEmpresa) {
    const result = await this.plugBoletoService.imprimirBoleto(
      boleto.integradorImpressaoId,
      dadosEmpresa.cnpjCpf,
    );
    if (!result.ok) {
      const body = await result.json();
      throw new Error(
        body?._mensagem ?? "Erro ao buscar dados de impressão do boleto",
      );
    }
    const dadosArquivo = await responseStreamHelper.getBlobPdf(result);
    const base64Data = await this.blobToBase64(dadosArquivo);
    const text = await this.buscarMensagemContexto(boleto, dadosEmpresa);
    const docName = `boleto-${dadosEmpresa.empresa.fantasia.replace(
      /\s+/g,
      "_",
    )}-${boleto.tituloDocumento.replace(/\s+/g, "")}.pdf`;

    return {
      number: celular,
      type: "document",
      file: base64Data,
      docName,
      text,
    };
  }

  async enviarMensagem(boleto, celular, dadosEmpresa) {
    try {
      const requestBody = await this.formatarMensagemAdzap(
        boleto,
        celular,
        dadosEmpresa,
      );
      const adzapResponse = await this.adzapService.enviarMensagem(requestBody);

      if (!adzapResponse.isAxiosError) {
        notification.sucessoGenericos("Mensagem enviada com sucesso!");
      } else {
        const message = adzapResponse?.response?.data?.message;
        throw new Error(message ?? "Erro interno de servidor");
      }
    } catch (error) {
      notification.erroGenericos(error?.message ?? "Erro interno de servidor");
    }
  }

  async openAbaBoleto(blob) {
    try {
      const url = URL.createObjectURL(blob);
      const pdfWindow = window.open(url, "_blank");
      if (!pdfWindow) {
        notification.alertaGenericos(
          "Popup bloqueado - por favor permita popups para este site",
        );
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Erro ao abrir PDF:", error);
      throw error;
    }
  }

  async imprimirBoleto(idImpressao, dadosEmpresa) {
    try {
      const result = await this.plugBoletoService.imprimirBoleto(
        idImpressao,
        dadosEmpresa.empresa.cnpjCpf,
      );
      if (!result.ok) {
        const body = await result.json();
        throw new Error(
          body?._mensagem ?? "Erro ao buscar dados de impressão do boleto",
        );
      }
      const blob = await responseStreamHelper.getBlobPdf(result);
      await this.openAbaBoleto(blob);
    } catch (error) {
      notification.erroGenericos(error?.message ?? "Erro interno de servidor");
    }
  }

  async adicionarBoletosUnicoPdf(listaIdImpressao, dadosEmpresa) {
    const doc = await PDFDocument.create();

    for (const idImpressao of listaIdImpressao) {
      const result = await this.plugBoletoService.imprimirBoleto(
        idImpressao,
        dadosEmpresa.empresa.cnpjCpf,
      );
      if (!result.ok) {
        const body = await result.json();
        throw new Error(
          body?._mensagem ?? "Erro ao buscar dados de impressão do boleto",
        );
      }
      const blob = await responseStreamHelper.getBlobPdf(result);
      const bytes = await blob.arrayBuffer();
      const pdf = await PDFDocument.load(bytes);
      const paginas = await doc.copyPages(pdf, pdf.getPageIndices());

      paginas.forEach((pagina) => doc.addPage(pagina));
    }

    const bytesFinais = await doc.save();

    const blobFinal = new Blob([bytesFinais], {
      type: "application/pdf",
    });

    return blobFinal;
  }

  async imprimirBoletosUnicoPdf(listaIdImpressao, dadosEmpresa) {
    try {
      const pdfCompleto = await this.adicionarBoletosUnicoPdf(
        listaIdImpressao,
        dadosEmpresa,
      );
      await this.openAbaBoleto(pdfCompleto);
    } catch (error) {
      notification.erroGenericos(error?.message ?? "Erro interno de servidor");
    }
  }

  async enviarMensagemUnicoPdf(
    listaIdImpressao,
    numeroNfSaida,
    celular,
    dadosEmpresa,
  ) {
    try {
      const pdfCompleto = await this.adicionarBoletosUnicoPdf(
        listaIdImpressao,
        dadosEmpresa,
      );
      const base64Data = await this.blobToBase64(pdfCompleto);
      const text = `Segue em anexo o(s) boleto(s) referente(s) ao pagamento da nota Nº ${numeroNfSaida}`;
      const docName = `boleto-${dadosEmpresa.empresa.fantasia.replace(
        /\s+/g,
        "_",
      )}-${numeroNfSaida.replace(/\s+/g, "")}.pdf`;

      const adzapResponse = await this.adzapService.enviarMensagem({
        number: celular,
        type: "document",
        file: base64Data,
        docName,
        text,
      });

      if (!adzapResponse.isAxiosError) {
        notification.sucessoGenericos("Mensagem enviada com sucesso!");
      } else {
        const message = adzapResponse?.response?.data?.message;
        throw new Error(message ?? "Erro interno de servidor");
      }
    } catch (error) {
      notification.erroGenericos(error?.message ?? "Erro interno de servidor");
    }
  }

  async enviarEmailUnicoPdf(
    listaIdImpressao,
    numeroNfSaida,
    cliente,
    dadosEmpresa,
  ) {
    try {
      const pdfCompleto = await this.adicionarBoletosUnicoPdf(
        listaIdImpressao,
        dadosEmpresa,
      );

      const base64Data = await this.blobToBase64(pdfCompleto);
      const anexo = {
        filename: `boleto-${cliente.nomeRazaoSocial?.replace(
          /\s+/g,
          "_",
        )}-${numeroNfSaida?.replace(/\s+/g, "")}.pdf`,
        type: "application/pdf",
        content: base64Data,
      };
      const resultEnvio = await this.notificacaoService.enviarEmail({
        to: cliente.email,
        subject: "Cobrança de pagamento via boleto",
        text: `Segue em anexo o(s) boleto(s) referente(s) ao pagamento da nota Nº ${numeroNfSaida}`,
        attachments: [anexo],
      });
      if (!resultEnvio.isAxiosError) {
        notification.sucessoGenericos("Boleto enviado com sucesso");
      } else {
        throw new Error("Erro ao enviar boleto via email");
      }
    } catch (error) {
      notification.erroGenericos(error?.message ?? "Erro interno de servidor");
    }
  }

  async enviarEmail(boleto, cliente, dadosEmpresa) {
    const getPrimeiroNome = (nomeCompleto) => {
      return nomeCompleto?.split(" ")[0] || "";
    };
    const { tituloDocumento, status, dataVencimento, valorTotal } = boleto;
    const texto = await this.emailPersonalizadoService.getInformacoesEmail({
      contexto: "boleto",
      apelidoFantasia: dadosEmpresa.empresa.fantasia,
      nomeCompletoCliente: cliente?.nomeRazaoSocial,
      primeiroNomeCliente: getPrimeiroNome(cliente?.nomeRazaoSocial),
      nomeUsuario: dadosEmpresa.usuario.nome,
      tituloDocumento,
      status,
      dataVencimento: format(new Date(dataVencimento), "dd/MM/yyyy"),
      valorTotal: formatPrice(valorTotal),
    });

    if (texto.isAxiosError) {
      const message = texto?.response?.data?.message;
      throw new Error(message ?? "Erro interno de servidor");
    }

    const {
      subject = "Cobrança de pagamento via boleto",
      text = "Segue boleto solicitado em anexo",
    } = texto.data ?? {};

    const result = await this.plugBoletoService.imprimirBoleto(
      boleto.integradorImpressaoId,
      dadosEmpresa.empresa.cnpjCpf,
    );
    if (!result.ok) {
      const body = await result.json();
      throw new Error(
        body?._mensagem ?? "Erro ao buscar dados de impressão do boleto",
      );
    }
    const dadosArquivo = await responseStreamHelper.getBlobPdf(result);
    const base64Data = await this.blobToBase64(dadosArquivo);
    const anexo = {
      filename: `boleto-${cliente.nomeRazaoSocial?.replace(
        /\s+/g,
        "_",
      )}-${boleto.tituloDocumento?.replace(/\s+/g, "")}.pdf`,
      type: "application/pdf",
      content: base64Data,
    };
    const resultEnvio = await this.notificacaoService.enviarEmail({
      to: cliente.email,
      subject,
      text,
      attachments: [anexo],
    });
    if (!resultEnvio.isAxiosError) {
      notification.sucessoGenericos("Boleto enviado com sucesso");
    } else {
      throw new Error("Erro ao enviar boleto via email");
    }
  }
}
export const boletosHelper = new BoletosHelper();
